<script setup>
// Tappy, our AI helper, answering one question on demo data, inside
// /features/setup-assistant: the REAL TappyAvatar (hello, thinking, talking,
// bounce) beside bubbles drawn with the chat's own classes, the reply
// rendered by the chat's own markdown renderer (useAssistantMarkdown), and the
// real hand-over button words (utils/tappy setupHandOverLabel). The intro is
// Tappy's own English greeting (TAPPY_FALLBACK). Nothing calls the API.
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import TappyAvatar from '~/components/assistant/TappyAvatar.vue';
import {useAssistantMarkdown} from '~/composables/useAssistantMarkdown';
import {TAPPY_DEMO_QUESTION, TAPPY_DEMO_REPLY, SETUP_DEMO_CLUB} from '~/utils/setupAssistantDemo';
import {TAPPY_FALLBACK, TAPPY_NAME, setupHandOverLabel} from '~/utils/tappy';

const {setAlertMessage} = useAlertService();
const {renderMarkdown} = useAssistantMarkdown(() => null);

const stage = ref(0); // 0 greeting, 1 asked, 2 thinking, 3 talking, 4 done
const shown = ref('');
let timers = [];
const later = (ms, fn) => { timers.push(setTimeout(fn, ms)); };
const clear = () => { timers.forEach(clearTimeout); timers = []; };
const replay = () => {
  clear();
  stage.value = 0;
  shown.value = '';
  later(1600, () => { stage.value = 1; later(700, () => { stage.value = 2; later(1300, () => {
    stage.value = 3;
    let i = 0;
    const tick = () => {
      i += 6;
      shown.value = TAPPY_DEMO_REPLY.slice(0, i);
      if (i < TAPPY_DEMO_REPLY.length) later(18, tick);
      else stage.value = 4;
    };
    tick();
  }); }); });
};
onMounted(replay);
onBeforeUnmount(clear);

const replyAnimation = computed(() => (stage.value === 2 ? 'thinking' : stage.value === 3 ? 'talking' : stage.value === 4 ? 'bounce' : 'idle'));
const handOver = setupHandOverLabel([SETUP_DEMO_CLUB], SETUP_DEMO_CLUB);
const demoOnly = () => setAlertMessage({message: 'On the real page this opens the setup assistant for your club.', color: 'info'});
</script>

<template>
  <div class="tds tl-card">
    <div class="tds-head">
      <div class="tds-title">
        <tappy-avatar :size="30" animation="peek"/>
        <span>{{ TAPPY_NAME }}</span>
        <span class="tds-title-sub">{{ TAPPY_FALLBACK.aiLabel }}</span>
      </div>
      <v-btn variant="text" size="small" prepend-icon="mdi-replay" class="text-none" @click="replay">Replay</v-btn>
    </div>

    <div class="tds-messages">
      <div class="assistant-row assistant">
        <tappy-avatar class="assistant-avatar assistant-avatar--intro" :size="56" animation="hello"/>
        <div class="assistant-bubble assistant">
          <div class="assistant-md"><p>{{ TAPPY_FALLBACK.intro }}</p></div>
        </div>
      </div>

      <div v-if="stage >= 1" class="assistant-row user">
        <div class="assistant-bubble user"><span class="assistant-text">{{ TAPPY_DEMO_QUESTION }}</span></div>
      </div>

      <div v-if="stage >= 2" class="assistant-row assistant">
        <tappy-avatar class="assistant-avatar" :size="24" :animation="replyAnimation"/>
        <div class="assistant-bubble assistant">
          <span v-if="stage === 2" class="assistant-typing">Thinking…</span>
          <div v-else class="assistant-md" v-html="renderMarkdown(shown)"/>
          <div v-if="stage === 4" class="assistant-handover">
            <v-btn size="small" color="primary" variant="tonal" prepend-icon="mdi-creation-outline" @click="demoOnly">{{ handOver }}</v-btn>
          </div>
        </div>
      </div>
    </div>

    <div class="tds-composer">
      <div class="tds-input"><span class="tds-placeholder">{{ TAPPY_FALLBACK.askLabel }}…</span></div>
      <span class="tds-send" aria-hidden="true"><v-icon size="20" icon="mdi-arrow-up" color="white"/></span>
    </div>
    <div class="tds-foot">Tappy is an AI helper and can make mistakes.</div>
  </div>
</template>

<style scoped lang="scss">
.tds {
  padding: 16px 16px 12px;
  display: flex;
  flex-direction: column;
}

.tds-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.tds-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 15px;
}

.tds-title-sub {
  font-weight: 500;
  font-size: 12px;
  color: rgb(var(--v-theme-charcoal));
  opacity: 0.75;
}

.tds-messages {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 2px 12px;
  min-height: 300px;
}

/* ── the chat's own classes (components/assistant/AssistantChat.vue) ── */
.assistant-row {
  display: flex;

  &.user { justify-content: flex-end; }
}

.assistant-avatar {
  margin-right: 8px;
  margin-top: 6px;
  flex: none;

  &--intro {
    margin-top: 0;
    margin-right: 10px;
  }
}

.assistant-bubble {
  max-width: 85%;
  border-radius: 14px;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.6;

  &.user { background: rgb(var(--v-theme-brandTint)); }

  &.assistant {
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.assistant-text { white-space: pre-wrap; word-break: break-word; }

.assistant-typing { color: rgb(var(--v-theme-textSubtle)); }

.assistant-md {
  word-break: break-word;

  :deep(p) {
    margin: 0 0 8px;

    &:last-child { margin-bottom: 0; }
  }

  :deep(ol),
  :deep(ul) {
    margin: 0 0 8px;
    padding-left: 20px;

    &:last-child { margin-bottom: 0; }
  }
}

.assistant-handover {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.tds-composer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.tds-input {
  flex: 1;
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-light));
  font-size: 14px;
}

.tds-placeholder { color: rgb(var(--v-theme-textSubtle)); }

.tds-send {
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-primary));
}

.tds-foot {
  margin-top: 8px;
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  text-align: center;
}
</style>
